/**
 * DeviceContextBanner — Phase 4
 *
 * Shows a contextual info bar when the current device type restricts
 * or changes the available experience. Appears at the top of the main layout.
 */

import React, { useState } from 'react';
import { Smartphone, Monitor, Ambulance, X, ChevronRight } from 'lucide-react';
import { useDevice } from '../context/DeviceContext';
import { useAuth } from '../hooks/useAuth';
import { Link } from 'react-router-dom';

const DISMISSED_KEY = 'aspis_device_banner_dismissed';

const DeviceContextBanner: React.FC = () => {
  const { deviceType, deviceLabel, isRegistered } = useDevice();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem(`${DISMISSED_KEY}_${deviceType}`) === '1';
  });

  if (!user || dismissed) return null;

  // Only show banner on BYOD when the user is a clinical role that benefits
  // from a registered ambulance tablet or base station
  const isUnregisteredByod = deviceType === 'byod' && !isRegistered;
  const isClinical = ['Paramedic', 'Nurse', 'Doctor', 'Manager', 'Admin'].includes(user.role);

  if (!isUnregisteredByod && isRegistered) return null;
  if (!isClinical) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(`${DISMISSED_KEY}_${deviceType}`, '1');
    setDismissed(true);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="relative flex items-center gap-3 px-4 py-2.5 bg-blue-50 dark:bg-blue-950/60 border-b border-blue-200 dark:border-blue-800 text-sm"
    >
      <Smartphone className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
      <span className="flex-1 text-blue-800 dark:text-blue-200">
        <strong>Personal device detected.</strong> Some clinical and operational features require a registered ambulance tablet or base station.{' '}
        <Link
          to="/device-setup"
          className="underline font-semibold hover:text-blue-900 dark:hover:text-blue-100 inline-flex items-center gap-0.5"
        >
          Register this device <ChevronRight className="w-3 h-3" />
        </Link>
      </span>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss device banner"
        className="p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900 text-blue-500 dark:text-blue-400 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default DeviceContextBanner;
